import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkActionsPanel = ({ 
  importedQRs = [], 
  selectedQRs = [], 
  onBulkSelection, 
  onBulkSave, 
  isProcessing = false 
}) => {
  const [showActions, setShowActions] = useState(false);

  const readyQRs = importedQRs?.filter(qr => qr?.status === 'ready');
  const allSelected = readyQRs?.length > 0 && selectedQRs?.length === readyQRs?.length;
  const someSelected = selectedQRs?.length > 0 && selectedQRs?.length < readyQRs?.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onBulkSelection(readyQRs?.map(qr => qr?.id), false);
    } else {
      onBulkSelection(readyQRs?.map(qr => qr?.id), true);
    }
  };

  const handleSelectReady = () => {
    const readyIds = readyQRs?.map(qr => qr?.id);
    onBulkSelection(readyIds, true);
  };

  const handleSelectNone = () => {
    onBulkSelection(selectedQRs, false);
  };

  const selectedReadyQRs = selectedQRs?.filter(id => {
    const qr = importedQRs?.find(q => q?.id === id);
    return qr && qr?.status === 'ready';
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Bulk Actions</h3>
        <Button
          onClick={() => setShowActions(!showActions)}
          variant="ghost"
          size="sm"
        >
          <Icon 
            name={showActions ? "ChevronUp" : "ChevronDown"} 
            size={16} 
          />
        </Button>
      </div>

      {/* Selection Summary */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600">
          {selectedQRs?.length === 0 ? (
            'No QR codes selected'
          ) : (
            `${selectedQRs?.length} of ${importedQRs?.length} QR codes selected`
          )}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-green-600">
            {selectedReadyQRs?.length} ready to save
          </span>
        </div>
      </div>

      {/* Bulk Selection Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={allSelected}
            ref={input => {
              if (input) input.indeterminate = someSelected;
            }}
            onChange={handleSelectAll}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label className="text-sm font-medium text-gray-700">
            Select All Ready
          </label>
        </div>

        {showActions && (
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleSelectReady}
              variant="outline"
              size="sm"
              disabled={readyQRs?.length === 0}
            >
              <Icon name="CheckCircle" size={16} className="mr-1" />
              Ready ({readyQRs?.length})
            </Button>
            <Button
              onClick={handleSelectNone}
              variant="outline"
              size="sm"
              disabled={selectedQRs?.length === 0}
            >
              <Icon name="X" size={16} className="mr-1" />
              None
            </Button>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-lg font-semibold text-blue-700">
            {importedQRs?.length}
          </div>
          <div className="text-blue-600">Total</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-lg font-semibold text-green-700">
            {readyQRs?.length}
          </div>
          <div className="text-green-600">Ready</div>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <div className="text-lg font-semibold text-red-700">
            {importedQRs?.filter(qr => qr?.status === 'error')?.length}
          </div>
          <div className="text-red-600">Errors</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-lg font-semibold text-purple-700">
            {selectedQRs?.length}
          </div>
          <div className="text-purple-600">Selected</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => onBulkSave(selectedReadyQRs)}
          disabled={selectedReadyQRs?.length === 0 || isProcessing}
          className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </>
          ) : (
            <>
              <Icon name="Database" size={16} className="mr-2" />
              Save Selected ({selectedReadyQRs?.length})
            </>
          )}
        </Button>

        <Button
          onClick={() => onBulkSave()}
          disabled={readyQRs?.length === 0 || isProcessing}
          variant="outline"
          className="flex-1"
        >
          <Icon name="Save" size={16} className="mr-2" />
          Save All Ready ({readyQRs?.length})
        </Button>
      </div>

      {/* Extended Actions (when shown) */}
      {showActions && (
        <div className="border-t border-gray-200 pt-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Additional Actions</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={selectedQRs?.length === 0}
              className="justify-center"
            >
              <Icon name="Edit" size={16} className="mr-2" />
              Bulk Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedQRs?.length === 0}
              className="justify-center"
            >
              <Icon name="Download" size={16} className="mr-2" />
              Export Selected
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedQRs?.length === 0}
              className="justify-center text-red-600 hover:text-red-700"
            >
              <Icon name="Trash2" size={16} className="mr-2" />
              Remove Selected
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActionsPanel;